import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
} from '@tanstack/react-query'

import { vehiclesApi } from '@/services/api/endpoints/vehiclesApi'
import type { DateRange, PlateVisitsParams } from '@/services/api/params'
import { queryKeys } from '@/services/api/queryKeys'
import type {
  PlateVisit,
  PlateVisitsPage,
  PlateVisitsStatus,
} from '@/services/api/schemas/vehicles'

/** Распознавания приходят непрерывно, поэтому показатели за период опрашиваем раз в минуту. */
const PERIOD_QUERY = { placeholderData: keepPreviousData, refetchInterval: 60_000 } as const

/** KPI «Всего посещений» и «Уникальные автомобили» (ТЗ п. 6.1, 6.2). range — из useMemo. */
export const useVehiclesSummary = (range: DateRange) =>
  useQuery({
    queryKey: queryKeys.vehiclesSummary(range),
    queryFn: () => vehiclesApi.getSummary(range),
    ...PERIOD_QUERY,
  })

/** Топ-5 частых автомобилей (ТЗ п. 6.4). */
export const useTopPlates = (range: DateRange) =>
  useQuery({
    queryKey: queryKeys.topPlates(range),
    queryFn: () => vehiclesApi.getTop(range),
    ...PERIOD_QUERY,
  })

/** График посещений по бакетам периода (ТЗ п. 6.3). */
export const usePlateVisitsChart = (range: DateRange) =>
  useQuery({
    queryKey: queryKeys.plateVisitsChart(range),
    queryFn: () => vehiclesApi.getChart(range),
    ...PERIOD_QUERY,
  })

export const PLATE_VISITS_PAGE_SIZE = 50

export interface PlateVisitsList {
  items: PlateVisit[]
  total: number
  /** Статус первой страницы: он описывает весь отфильтрованный журнал. */
  status: PlateVisitsStatus
}

/** Склейка страниц с удалением дублей по id: новые распознавания сдвигают offset между запросами. */
function flattenPages(data: InfiniteData<PlateVisitsPage>): PlateVisitsList {
  const seen = new Set<string>()
  const items: PlateVisit[] = []
  for (const page of data.pages) {
    for (const visit of page.data) {
      if (seen.has(visit.id)) continue
      seen.add(visit.id)
      items.push(visit)
    }
  }
  const last = data.pages[data.pages.length - 1]
  return { items, total: last.total, status: data.pages[0].status }
}

/**
 * История посещений (ТЗ п. 6.5) страницами по 50 с подгрузкой при прокрутке, как журнал событий.
 * params (период и поиск) держать в useMemo, иначе ключ запроса меняется каждый рендер.
 */
export const usePlateVisits = (params: Pick<PlateVisitsParams, 'from' | 'to' | 'plate'>) =>
  useInfiniteQuery({
    queryKey: queryKeys.plateVisits({ ...params, limit: PLATE_VISITS_PAGE_SIZE }),
    queryFn: ({ pageParam }) =>
      vehiclesApi.getVisits({ ...params, limit: PLATE_VISITS_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.offset + last.data.length
      return next < last.total && last.data.length > 0 ? next : undefined
    },
    select: flattenPages,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  })
